import type { ComponentProps } from '../blocks';
import { CHART_COLORS, CHART_FONT, resolveCssColor, type ChartColors } from './colors';
import { xmlEscape } from './escape';

/** Layout constants for the rasterized bar chart. */
export const BARS_DIMS = {
  W: 720,
  rowH: 22,
  rowGap: 14,
  labelW: 180,
  valueW: 72,
  gap: 12,
} as const;

/** Bar width as a percentage of the track, matching the React primitive (min 1.5%). */
export function barFraction(value: number, max: number): number {
  return Math.max((value / (max || 1)) * 100, 1.5);
}

/**
 * Standalone horizontal bar-chart SVG for rasterization (Slack PNGs). The React
 * primitive renders bars with CSS grid; this mirrors the same scale and colors
 * as static SVG so the two surfaces read the same.
 */
export function barsSvg(props: ComponentProps['Bars'], colors: ChartColors = CHART_COLORS): string {
  const { title, data, unit = '' } = props;
  const { W, rowH, rowGap, labelW, valueW, gap } = BARS_DIMS;
  const titleH = title ? 34 : 8;
  const max = Math.max(...data.map((d) => d.value), 1);
  const trackX = labelW + gap;
  const trackW = W - labelW - valueW - gap * 2;
  const totalH = titleH + data.length * (rowH + rowGap);
  const unitSuffix = unit && unit.length < 4 ? unit : '';

  const rows = data
    .map((d, i) => {
      const y = titleH + i * (rowH + rowGap);
      const barW = (barFraction(d.value, max) / 100) * trackW;
      const fill = resolveCssColor(d.color, colors);
      return (
        `<text x="${labelW}" y="${y + rowH / 2 + 4}" text-anchor="end" font-size="13" fill="${colors.subtext}">${xmlEscape(d.label)}</text>` +
        `<rect x="${trackX}" y="${y}" width="${trackW}" height="${rowH}" rx="8" fill="${colors.track}"/>` +
        `<rect x="${trackX}" y="${y}" width="${barW.toFixed(1)}" height="${rowH}" rx="8" fill="${xmlEscape(fill)}"/>` +
        `<text x="${W}" y="${y + rowH / 2 + 4}" text-anchor="end" font-size="13" font-weight="600" fill="${colors.text}">${xmlEscape(d.value.toLocaleString() + unitSuffix)}</text>`
      );
    })
    .join('');

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${totalH}" width="${W}" height="${totalH}" font-family='${CHART_FONT}'>` +
    `<rect width="${W}" height="${totalH}" fill="${colors.bg}"/>` +
    (title ? `<text x="0" y="22" font-size="15" font-weight="600" fill="${colors.text}">${xmlEscape(title)}</text>` : '') +
    rows +
    `</svg>`
  );
}
